export interface ClearbitAccount {
  id: string;
  email: string;
  domain: string;
  orgId: number;
  dateCreated: string;
}

// https://clearbit.com/docs#reveal-api-lookup-ip-addresses
export interface ClearbitRevealBaseData {
  ip: string;
  fuzzy: boolean;
  type: 'isp' | 'company' | 'education' | 'government';
}

export interface ClearbitRevealCompanyData extends ClearbitRevealBaseData {
  type: 'company' | 'education' | 'government';
  domain: string;
  company: ClearbitRevealCompany | null;
  geoIP: ClearbitGeoIP | null;
  confidenceScore: ClearbitConfidenceScore;
}

export interface ClearbitRevealISPData extends ClearbitRevealBaseData {
  type: 'isp';
}

export type ClearbitRevealData = ClearbitRevealCompanyData | ClearbitRevealISPData;

interface ClearbitGeoIP {
  city: string;
  country: string;
  countryCode: string;
  state: string;
  stateCode: string;
}

// All fields except id and name are potentially falsy / undefined.
export interface ClearbitRevealCompany {
  id: string;
  name: string;
  legalName?: string;
  domain?: string;
  domainAliases?: string[];
  site?: {
    phoneNumbers?: string[];
    emailAddresses?: string[];
  };
  category?: {
    sector?: string;
    industryGroup?: string;
    industry?: string;
    subIndustry?: string;
    sicCode?: string;
    naicsCode?: string;
  };
  tags: string[];
  description?: string;
  foundedYear?: number;
  location?: string;
  timeZone?: string;
  utcOffset?: number;
  geo?: {
    streetNumber?: string;
    streetName?: string;
    subPremise?: string;
    city?: string;
    postalCode?: string;
    state?: string;
    stateCode?: string;
    country?: string;
    countryCode?: string;
    lat?: string;
    lng?: string;
  };
  logo?: string;
  facebook?: {
    handle?: string;
  };
  linkedin?: {
    handle?: string;
  };
  twitter?: {
    handle?: string;
    id?: string;
    bio?: string;
    followers?: number;
    following?: number;
    location?: string;
    site?: string;
    avatar?: string;
  };
  crunchbase?: {
    handle?: string;
  };
  emailProvider?: boolean;
  type?: string;
  ticker?: string;
  identifiers?: {
    usEIN?: string;
  };
  phone?: string;
  indexedAt: string;
  metrics?: {
    alexaUsRank?: number;
    alexaGlobalRank?: number;
    employees?: number;
    employeesRange?: string;
    marketCap?: number;
    raised?: number;
    annualRevenue?: number;
    estimatedAnnualRevenue?: string;
    fiscalYearEnd?: number;
  };
  tech?: string[];
  techCategories?: string[];
  parent?: {
    domain?: string;
  };
  ultimateParent?: {
    domain?: string;
  };
}

export interface ClearbitVisitorRevealData {
  uniqueVisitorId: string;
  ipAddress: string;
  dateCreated: string;
  data: ClearbitRevealData;
}

export enum ClearbitSixSenseRevealEventType {
  ANY = 'ANY',
  ALERT = 'ALERT',
  CHAT = 'CHAT',
  DIAL = 'DIAL',
  WIDGET = 'WIDGET',
}

export interface ClearbitSixSenseOrgIntegrationData {
  apiSecret: string;
  revealEvents: ClearbitSixSenseRevealEventType[];
  revealMatchingSegments?: number[];
  revealMatchingConditions?: any;
  excludeMatchingSegments?: number[];
  excludeMatchingConditions?: any;
}

export enum ClearbitConfidenceScore {
  VERY_HIGH = 'very_high',
  HIGH = 'high',
  MEDIUM = 'medium',
  LOW = 'low',
}
